import { threadDay } from '@adhd/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { OwnLifeEvent } from '../lib/api.js';

/**
 * The threads running on a day, as chips — THE one rendering of a thread
 * wherever a day is shown (docs/today-rework-2026-10-14; 14 Oct 2026). Under
 * the word on Today, at the top of a day in Days and in the list. Tapping one
 * opens the thread's page, which is where its controls live. Extracted from
 * Conditions, which draws the same chips with "+ thread" and People after them.
 *
 * A thread is a life event, big or small — something shaping the days. An
 * event (the big thing) keeps its ochre edge; a thread, allowed to be
 * ordinary, gets a plain one. The count is his own and never reaches a link.
 */
export function ThreadChips({ events, day, children }: { events: OwnLifeEvent[]; day: string; children?: ReactNode }): JSX.Element | null {
  if (events.length === 0 && !children) return null;
  return (
    <ul className="threads" aria-label="Going on">
      {events.map((e) => (
        <li key={e.id}>
          <Link className={`thr${e.kind === 'thread' ? ' thr-thread' : ''}`} to={`/life-event/${e.id}`}>
            <b>{e.label?.trim() || 'Something big'}</b>
            <span className="thr-n">{sinceLine(e.countFrom ?? e.startedOn, day, e.kind === 'thread' && !!e.countFrom)}</span>
          </Link>
        </li>
      ))}
      {children}
    </ul>
  );
}

/** "since 4 Sep · day 12", or just "day 12" for a thread counted from a date he chose. */
export function sinceLine(from: string, today: string, countOnly = false): string {
  /* The maths is `threadDay` in @adhd/shared, because LifeEventPage had its
     own copy that clamped the not-yet-started case to "day 1" while this one
     said "from 1 Nov". See the comment on `threadDay`. */
  const { days, before } = threadDay(from, today);
  const since = new Date(`${from}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (before) return `counts from ${since}`;
  return countOnly ? `day ${days}` : `since ${since} · day ${days}`;
}
